import { useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { LoadingPane } from '~/components/loading-pane';
import {
  FolderContextDocument,
  FolderContextProvider,
  findLandingFolderId,
  useAppContext,
} from '~/contexts';
import { useLocation } from '~/hooks/location';

/**
 * Handles the grid/list view on all media pages and makes sure the folder tree context is set.
 */
export const ContentRoot = () => {
  const { currentNetwork } = useAppContext();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const lastView = useRef(searchParams.get('view')?.trim() || 'Grid');
  const search = searchParams.get('search')?.trim() || '';
  const backToFolder = searchParams.get('backToFolder')?.trim() || '';

  useEffect(() => {
    const currentView = searchParams.get('view')?.trim();
    if (currentView === lastView.current) return;
    const view = currentView || lastView.current;
    setSearchParams(
      {
        ...(backToFolder && { backToFolder }),
        ...(search && { search }),
        view,
      },
      { replace: true, state: location.state },
    );
    lastView.current = view;
  }, [backToFolder, location.state, search, searchParams, setSearchParams]);

  const { data, loading } = useQuery(FolderContextDocument, {
    fetchPolicy: 'cache-first',
    variables: { networkId: currentNetwork.id },
  });

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {data?.network?.contentFolderTree && (
        <FolderContextProvider
          folderTree={data.network.contentFolderTree}
          landingFolderId={findLandingFolderId(
            data.network.contentFolderTree,
            data.network.contentFolders,
          )}
        >
          <Outlet />
        </FolderContextProvider>
      )}
    </LoadingPane>
  );
};
